
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useCoordinateUpdates } from "./useCoordinateUpdates";
import { useProfileDebug } from "./useProfileDebug";

export interface RadiusMatchingStats {
  radiusMatches: number;
  stateMatches: number;
  regionalMatches: number;
  cityMatches: number;
  totalMatches: number;
}

export const useRadiusMatching = () => {
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<RadiusMatchingStats | null>(null);
  const { toast } = useToast();
  
  const { updateProfileCoordinates, loading: coordinateLoading } = useCoordinateUpdates();
  const { debugProfileReports } = useProfileDebug();

  const triggerRadiusMatching = async () => {
    try {
      setLoading(true);
      console.log('🎯 Starting enhanced radius-based matching...');
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('No authenticated user found');
      }

      console.log('User ID:', user.id);

      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('id, location, latitude, longitude')
        .eq('user_id', user.id)
        .single();

      if (profileError) {
        console.error('❌ Error fetching profile:', profileError);
        throw profileError;
      }

      if (!profile) {
        console.log('❌ No profile found');
        throw new Error('No profile found for the current user');
      }

      if (!profile.location) {
        console.log('❌ No location set in profile');
        throw new Error('Profile location is required for coordinate-based matching');
      }

      if (!profile.latitude || !profile.longitude) {
        console.log('⚠️ Coordinates missing, attempting to update...');
        await updateProfileCoordinates();
        
        const { data: updatedProfile } = await supabase
          .from('profiles')
          .select('id, location, latitude, longitude')
          .eq('user_id', user.id)
          .single();
          
        if (!updatedProfile?.latitude || !updatedProfile?.longitude) {
          toast({
            title: "Coordinate Issue",
            description: `Unable to determine coordinates for location: ${profile.location}. Please check if your location format is supported.`,
            variant: "destructive"
          });
          return;
        }
      }

      console.log('📊 Profile coordinates confirmed:', {
        latitude: profile.latitude,
        longitude: profile.longitude
      });

      console.log('🚀 Triggering enhanced matching...');
      const { data, error } = await supabase.functions.invoke('fetch-usda-data', {
        body: { 
          matchProfiles: true,
          userId: user.id
        }
      });

      if (error) {
        console.error('Function invoke error:', error);
        throw error;
      }

      console.log('Enhanced matching response:', data);

      const { data: matchStats } = await supabase
        .from('profile_reports')
        .select('relevance_score')
        .eq('profile_id', profile.id);

      if (matchStats) {
        const radiusMatches = matchStats.filter(m => m.relevance_score >= 90).length;
        const stateMatches = matchStats.filter(m => m.relevance_score >= 70 && m.relevance_score < 90).length;
        const regionalMatches = matchStats.filter(m => m.relevance_score >= 50 && m.relevance_score < 70).length;
        const cityMatches = matchStats.filter(m => m.relevance_score < 50).length;

        const statsData = {
          radiusMatches,
          stateMatches,
          regionalMatches,
          cityMatches,
          totalMatches: matchStats.length
        };
        
        setStats(statsData);
        console.log('📊 Matching statistics:', statsData);
      }

      console.log('✅ Enhanced radius-based matching completed');
      
      toast({
        title: "Enhanced Matching Complete",
        description: `Reports matched successfully. Found ${stats?.totalMatches || 0} matching reports.`,
      });

    } catch (error: any) {
      console.error('❌ Error in radius matching:', error);
      
      toast({
        title: "Matching Error",
        description: error.message || "Failed to perform enhanced radius-based matching",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    triggerRadiusMatching,
    updateProfileCoordinates,
    debugProfileReports,
    loading: loading || coordinateLoading,
    stats
  };
};
